import React, { useEffect, useRef, useState } from "react";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
  PaginationComponent,
  Row,
} from "../../../../../../components/Component";
import { Alert, Spinner } from "reactstrap";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import TextInput from "../../../../../components/inputs/TextInput";
import { messageToast } from "../../../../../../utils/Utils";
import * as app_consts from "../../../../../../constants/react_app_consts";
import { IS_EMPTY_OR_WHITESPACE } from "../../../../../../utils/RegexUtils";
import { employee_dashboard_actions_helper } from "../../../../../../actions/actions_helper";
import {
  fail_add_grn_scheduler,
  loading_add_grn_scheduler,
  reset_add_grn_scheduler,
  success_add_grn_scheduler,
} from "../../../../../../reducers/dashboard_reducers/warehouse/add_grn_scheduler_Slice";
import {
  ADD_GRN_SCHEDULER_LIST_ACCESS_DENIED_MSG,
  ADD_GRN_SCHEDULER_LIST_ERR_MSG,
  ADD_GRN_SCHEDULER_LIST_SUCCESS_MSG,
} from "../../../../../../constants/success_error_messages_consts";
import { ADD_GRN_SCHEDULER_API } from "../../../../../../api/api_data";
import moment from "moment";
import { ExportToCsv } from "export-to-csv";

const initialState = { name: "" };

// Backend expected keys 
// final payload keays
const BACKEND_KEYS = [
  "horse_reg",
  "trailer_reg_one",
  "trailer_reg_two",
  "driver",
  "transporter",
  "offload_point",
  "tonnage", // OPTIONAL 
  "cargo_details",
  "date_loaded",
  "load_reference",
];

// Headers to generate in template (keep tonnage column even if optional)
const csv_headers = [...BACKEND_KEYS];

// normalize header text from CSV
const normalizeHeader = (h) =>
  (h || "")
    .replace(/"/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/\./g, "");

// aliases so tracking CSV can be accepted
const HEADER_ALIASES = {
  trailer_reg: "trailer_reg_one",
  destination: "offload_point",
  // allow other variants if they appear
  trailer_reg_1: "trailer_reg_one",
  trailer_reg1: "trailer_reg_one",
  trailer_reg_2: "trailer_reg_two",
  trailer_reg2: "trailer_reg_two",
};

// Minimal headers required (tonnage optional)
const MIN_REQUIRED_HEADERS = [
  "horse_reg",
  "trailer_reg_one", // can come from trailer_reg
  "driver",
  "transporter",
  "offload_point", // can come from destination
  "cargo_details",
  "date_loaded",
  "load_reference",
];

// pick only backend fields
const pickBackendFields = (row) => ({
  horse_reg: row?.horse_reg ?? "",
  trailer_reg_one: row?.trailer_reg_one ?? "",
  trailer_reg_two: row?.trailer_reg_two ?? "",
  driver: row?.driver ?? "",
  transporter: row?.transporter ?? "",
  offload_point: row?.offload_point ?? "",
  tonnage: row?.tonnage ?? "", // optional
  cargo_details: row?.cargo_details ?? "",
  date_loaded: row?.date_loaded ?? "",
  load_reference: row?.load_reference ?? "",
});

const AddGRNScheduleModal = ({ successFunction, setModalLoading }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [errorVal, setError] = useState("");
  const [formData, setFormData] = useState(initialState);
  const [loading_message, setLoadingMessage] = useState("");

  const [failed_imports, setFailedImports] = useState([]);
  const [successImports, setSuccessImports] = useState([]);
  const [imported_data, setImportedData] = useState([]);
  const [imported_data_list, setImportedDataList] = useState([]);

  const [successImportsPerPage] = useState(10);
  const [currentSuccessImportsPage, setSuccessImportsPage] = useState(1);

  const [failedImportsPerPage] = useState(10);
  const [currentFailedImportsPage, setSuccessFailedImportsPage] = useState(1);

  const fileInputRef = useRef(null);

  const failedImportsPaginate = (pageNumber) => setSuccessFailedImportsPage(pageNumber);
  const successImportsPaginate = (pageNumber) => setSuccessImportsPage(pageNumber);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handlerAddGrnSchedule = () => {
    setError("");
    dispatch(
      employee_dashboard_actions_helper(
        loading_add_grn_scheduler,
        success_add_grn_scheduler,
        fail_add_grn_scheduler,
        ADD_GRN_SCHEDULER_LIST_ERR_MSG,
        ADD_GRN_SCHEDULER_LIST_SUCCESS_MSG,
        app_consts.POST,
        ADD_GRN_SCHEDULER_API,
        { name: formData?.name, products: successImports },
        ADD_GRN_SCHEDULER_LIST_ACCESS_DENIED_MSG
      )
    );
  };

  // Listener
  const add_grn_scheduler_listener = useSelector((state) => state.add_grn_scheduler.add_grn_scheduler);

  useEffect(() => {
    if (add_grn_scheduler_listener.status !== app_consts.IDLE)
    {
      if (add_grn_scheduler_listener.status === app_consts.LOADING)
      {
        setError("");
        setLoading(true);
        setModalLoading(true);
      } else
      {
        if (add_grn_scheduler_listener.status === app_consts.FAIL)
        {
          setError(add_grn_scheduler_listener.message || ADD_GRN_SCHEDULER_LIST_ERR_MSG);
          messageToast(add_grn_scheduler_listener.message || ADD_GRN_SCHEDULER_LIST_ERR_MSG, app_consts.FAIL);
        } else if (add_grn_scheduler_listener.status === app_consts.SUCCESS)
        {
          successFunction();
        }
        setLoading(false);
        setModalLoading(false);
        dispatch(reset_add_grn_scheduler());
      }
    }
  }, [add_grn_scheduler_listener, dispatch, setModalLoading, successFunction]);

  const handleCsvUpload = (event) => {
    setError("");
    const file = event?.target?.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    setLoading(true);
    setLoadingMessage("Reading CSV File");

    reader.onload = (e) => {
      const csv_data = e?.target?.result;
      convertToJson(csv_data);
    };

    reader.onerror = () => {
      setLoading(false);
      setLoadingMessage("");
      messageToast("Error reading file", app_consts.FAIL);
    };

    reader.readAsText(file);
  };

  const convertToJson = (csv_data) => {
    const lines = (csv_data || "")
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    if (lines.length < 2)
    {
      messageToast("CSV file has no data rows.", app_consts.FAIL);
      setLoading(false);
      setLoadingMessage("");
      return;
    }

    // 1) normalize incoming headers
    const incomingHeaders = lines[0].split(",").map((h) => h.trim()).map(normalizeHeader);

    // 2) apply aliases (tracking CSV support)
    const mappedHeaders = incomingHeaders.map((h) => HEADER_ALIASES[h] || h);

    // 3) validate minimal required headers exist (order doesn't matter)
    const missing = MIN_REQUIRED_HEADERS.filter((h) => !mappedHeaders.includes(h));
    if (missing.length > 0)
    {
      messageToast(`Invalid headers. Missing: ${missing.join(", ")}`, app_consts.FAIL);
      setLoading(false);
      setLoadingMessage("");
      return;
    }

    const result = [];

    for (let i = 1; i < lines.length; i++)
    {
      const values = lines[i].split(",").map((v) => v.trim());
      if (values.every((v) => v === "")) continue;

      const row = {};
      mappedHeaders.forEach((h, idx) => {
        row[h] = values[idx] ?? "";
      });

      // build payload row with backend expected keys
      result.push(pickBackendFields(row));
    }

    if (result.length === 0)
    {
      messageToast("No Data has been Found.", app_consts.FAIL);
      setLoading(false);
      setLoadingMessage("");
      return;
    }

    // reset state for new file
    setFailedImports([]);
    setSuccessImports([]);
    setImportedData([]);
    setImportedDataList([]);

    setImportedData(result);
    setImportedDataList(result);
  };

  useEffect(() => {
    if (imported_data?.length > 0)
    {
      validate_uploaded_csv_data(imported_data[0]);
    } else
    {
      setLoadingMessage("");
      checkImportedDataList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imported_data]);

  const checkImportedDataList = () => {
    if (
      failed_imports.length + successImports.length === imported_data_list.length &&
      imported_data_list.length !== 0
    )
    {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  const validate_uploaded_csv_data = (sent_data) => {
    const copy_sent_data = sent_data;
    const errors = [];

    // making tonnage optional and removing it from the required list: NOTE
    const fields = [
      { key: "horse_reg", label: "Horse Reg is Required" },
      { key: "driver", label: "Driver is Required" },
      { key: "transporter", label: "Transporter is Required" },
      { key: "offload_point", label: "Offload Point is Required" },
      { key: "cargo_details", label: "Cargo Details is Required" },
      { key: "date_loaded", label: "Date Loaded is Required" },
      { key: "load_reference", label: "Load Reference is Required" },
    ];

    fields.forEach(({ key, label }) => {
      const isInvalid = IS_EMPTY_OR_WHITESPACE.test(copy_sent_data?.[key]);
      if (isInvalid) errors.push(label);
    });

    // Duplicate check: ignores tonnage because it optional
    const normalize = (v) => (v ?? "").toString().trim().toLowerCase();
    const duplicate = imported_data?.filter((data) => {
      return (
        normalize(data?.horse_reg) === normalize(copy_sent_data?.horse_reg) &&
        normalize(data?.driver) === normalize(copy_sent_data?.driver) &&
        normalize(data?.transporter) === normalize(copy_sent_data?.transporter) &&
        normalize(data?.offload_point) === normalize(copy_sent_data?.offload_point) &&
        normalize(data?.cargo_details) === normalize(copy_sent_data?.cargo_details) &&
        normalize(data?.date_loaded) === normalize(copy_sent_data?.date_loaded) &&
        normalize(data?.load_reference) === normalize(copy_sent_data?.load_reference)
      );
    });

    if (duplicate?.length > 1) errors.push("Duplicate Entry");

    if (errors.length > 0)
    {
      setFailedImports((prev) => [...prev, { ...copy_sent_data, errors }]);
    } else
    {
      setSuccessImports((prev) => [...prev, { ...copy_sent_data }]);
    }

    // Removes first item and continue
    const updated_import_data = imported_data.filter((_, index) => index !== 0);
    setImportedData(updated_import_data);
  };

  const handleReUploadClick = () => {
    setFailedImports([]);
    setSuccessImports([]);
    setImportedData([]);
    setImportedDataList([]);
    setLoadingMessage("");
    setError("");

    if (fileInputRef.current)
    {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const indexOfLastSuccessImport = currentSuccessImportsPage * successImportsPerPage;
  const indexOfFirstSuccessImport = indexOfLastSuccessImport - successImportsPerPage;
  const currentSuccessImports = successImports.slice(indexOfFirstSuccessImport, indexOfLastSuccessImport);

  const indexOfLastFailedImport = currentFailedImportsPage * failedImportsPerPage;
  const indexOfFirstFailedImport = indexOfLastFailedImport - failedImportsPerPage;
  const currentFailedImports = failed_imports.slice(indexOfFirstFailedImport, indexOfLastFailedImport);

  const export_to_csv = () => {
    const cur_dt = moment().format("DD-MM-YYYY h:mm:ss a");

    const options = {
      fieldSeparator: ",",
      quoteStrings: '"',
      showLabels: true,
      showTitle: false,
      useTextFile: false,
      useBom: true,
      filename: `United African Grain GRN Schedule CSV Template (as of ${cur_dt})`,
      useKeysAsHeaders: false,
      headers: csv_headers,
    };

    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv([{}]); // blank template row
  };

  return (
    <>
      {errorVal && (
        <div className="mb-3 w-100">
          <Alert color="danger" className="alert-icon">
            <Icon name="alert-circle" /> {errorVal}{" "}
          </Alert>
        </div>
      )}

      <BlockDes className="text-soft fs-16px">
        <p>
          To Download CSV Template,{" "}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              export_to_csv();
            }}
            style={{ textDecoration: "underline" }}
            className="text-underline"
          >
            Click Here
          </a>
        </p>
      </BlockDes>

      <form onSubmit={(e) => e.preventDefault()}>
        <Row className="g-4">
          <TextInput
            label="Name"
            required
            type="text"
            id_val="name"
            maxlength="100"
            value={formData.name}
            colSize="2"
            name="name"
            handleChange={handleChange}
          />

          <Col xl="12" className="d-flex justify-content-start">
            <Button
              onClick={handlerAddGrnSchedule}
              size="lg"
              type="submit"
              color="primary"
              disabled={loading ? loading : !(formData?.name && successImports?.length > 0)}
            >
              Add GRN Schedule
            </Button>
          </Col>
        </Row>

        {loading ? (
          <div className="p-5 d-flex align-center justify-center d-flex flex-column">
            <span style={{ fontSize: 16, marginBottom: 2 }} className="text-soft">
              {loading_message}
            </span>
            <Spinner />
          </div>
        ) : (
          <>
            {failed_imports.length + successImports.length === imported_data_list.length &&
              imported_data_list.length !== 0 ? (
              <>
                <Block>
                  <BlockBetween className="mb-4">
                    <BlockHeadContent />
                    <BlockHeadContent>
                      <div className="card-inner">
                        <Button color="primary" size="sm" onClick={handleReUploadClick}>
                          Re-Upload CSV
                        </Button>
                      </div>
                    </BlockHeadContent>
                  </BlockBetween>
                </Block>

                {/*Valid Data */}
                <div className="card-inner">
                  <Block>
                    <BlockBetween className="mb-4">
                      <BlockHeadContent>
                        <BlockTitle tag="h5">Valid Data (To Be Imported)</BlockTitle>
                      </BlockHeadContent>
                    </BlockBetween>

                    {successImports?.length > 0 ? (
                      <>
                        <div className="nk-tb-list is-separate is-medium mb-3">
                          <DataTableHead className="nk-tb-item">
                            <DataTableRow><span className="sub-text">Horse Reg</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Trailer Reg 1</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Trailer Reg 2</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Driver</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Transporter</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Offload Point</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Tonnage (Optional)</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Cargo Details</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Date Loaded</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Load Reference</span></DataTableRow>
                          </DataTableHead>

                          {currentSuccessImports.map((item, idx) => (
                            <DataTableItem key={idx}>
                              <DataTableRow><span className="tb-sub">{item?.horse_reg || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.trailer_reg_one || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.trailer_reg_two || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.driver || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.transporter || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.offload_point || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.tonnage || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.cargo_details || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.date_loaded || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.load_reference || "--"}</span></DataTableRow>
                            </DataTableItem>
                          ))}
                        </div>

                        <div className="mt-3">
                          <PaginationComponent
                            itemPerPage={successImportsPerPage}
                            totalItems={successImports.length}
                            paginate={successImportsPaginate}
                            currentPage={currentSuccessImportsPage}
                          />
                        </div>
                      </>
                    ) : (
                      <span style={{ fontSize: 16 }} className="text-soft">
                        No Valid Data Found
                      </span>
                    )}
                  </Block>
                </div>

                <hr />

                {/* Invalid Data */}
                <div className="card-inner">
                  <Block>
                    <BlockBetween className="mb-4">
                      <BlockHeadContent>
                        <BlockTitle tag="h5">Invalid Data (Not To Be Imported)</BlockTitle>
                      </BlockHeadContent>
                    </BlockBetween>

                    {failed_imports?.length > 0 ? (
                      <>
                        <div className="nk-tb-list is-separate is-medium mb-3">
                          <DataTableHead className="nk-tb-item">
                            <DataTableRow><span className="sub-text">Horse Reg</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Trailer Reg 1</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Trailer Reg 2</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Driver</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Transporter</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Offload Point</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Tonnage (Optional)</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Cargo Details</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Date Loaded</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Load Reference</span></DataTableRow>
                          </DataTableHead>

                          {currentFailedImports.map((item, idx) => (
                            <DataTableItem key={idx}>
                              <DataTableRow><span className="tb-sub">{item?.horse_reg || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.trailer_reg_one || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.trailer_reg_two || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.driver || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.transporter || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.offload_point || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.tonnage || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.cargo_details || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.date_loaded || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.load_reference || "--"}</span></DataTableRow>
                            </DataTableItem>
                          ))}
                        </div>

                        <div className="mt-3">
                          <PaginationComponent
                            itemPerPage={failedImportsPerPage}
                            totalItems={failed_imports.length}
                            paginate={failedImportsPaginate}
                            currentPage={currentFailedImportsPage}
                          />
                        </div>
                      </>
                    ) : (
                      <span style={{ fontSize: 16 }} className="text-soft">
                        No Invalid Data Found
                      </span>
                    )}
                  </Block>
                </div>

                {/*Single input (no duplicate id issues) */}
                <input
                  type="file"
                  id="csvUpload"
                  accept=".csv,text/csv"
                  style={{ display: "none" }}
                  onChange={handleCsvUpload}
                  ref={fileInputRef}
                />
              </>
            ) : (
              <div
                className="product-thumb mt-4"
                style={{
                  height: "250px",
                  width: "100%",
                  position: "relative",
                  border: "1px dashed #ccc",
                  backgroundColor: "#f9f9f9",
                  overflow: "hidden",
                }}
              >
                <label
                  htmlFor="csvUpload"
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    color: "#777",
                    fontSize: "16px",
                    fontWeight: "bold",
                    fontFamily: "Arial, sans-serif",
                    cursor: "pointer",
                    textAlign: "center",
                  }}
                >
                  <p style={{ margin: 0 }}>Click to Upload CSV File</p>
                </label>

                <input
                  type="file"
                  id="csvUpload"
                  accept=".csv,text/csv"
                  style={{ display: "none" }}
                  onChange={handleCsvUpload}
                  ref={fileInputRef}
                />
              </div>
            )}
          </>
        )}
      </form>
    </>
  );
};

export default AddGRNScheduleModal;
